import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentSession } from "../../../supabase/authService";
import { supabase } from "../../../supabase/supabaseClient";
import { useToast } from "../../../context/CustomToast";
import { useGetCurrentUserQuery } from "../../../store/api/postgresUserApi";
import { getReturnUrl } from "../../../utils/marketingParams";

const APP_LOGIN_URL = process.env.REACT_APP_APP_HOME_URL
  ? `${process.env.REACT_APP_APP_HOME_URL}/login`
  : "https://app.pedagogue.io/login";
const APP_HOME_URL =
  process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io";

export type VerifyStatus =
  | "checking"
  | "success"
  | "error"
  | "needs_job_title"
  | "otp_entry";

export const useVerifyHook = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const hasHandledError = useRef(false);

  const [status, setStatus] = useState<VerifyStatus>("checking");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [wizardId, setWizardId] = useState<string | null>(null);

  // OTP entry state (for when no tokens/code are present in URL)
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);

  // Parse OTP context from query params
  const parseQueryParams = () => {
    const query = new URLSearchParams(window.location.search);
    return {
      wizardId: query.get("wizard_id") || "",
      email: query.get("email") || "",
    };
  };

  // Bootstrap: existing sessions continue; otherwise show code entry.
  useEffect(() => {
    const bootstrap = async () => {
      try {
        const queryParams = parseQueryParams();

        // Capture return_to from verify URL query params.
        const urlReturnTo = new URLSearchParams(window.location.search).get("return_to");
        if (urlReturnTo) {
          sessionStorage.setItem("pedagogue_marketing_returnTo", urlReturnTo);
        }

        // Store wizard_id if present (retired flow; see redirectAfterVerify)
        if (queryParams.wizardId) {
          setWizardId(queryParams.wizardId);
        }

        // Pre-fill email from query param (for OTP entry fallback)
        if (queryParams.email) {
          setOtpEmail(queryParams.email);
          // A requested code takes precedence over any previous identity.
          setStatus("otp_entry");
          return;
        }

        const session = await getCurrentSession();
        if (session?.user) {
          setStatus("success");
          return;
        }

        setStatus("otp_entry");
      } catch (error) {
        console.error("Error during verification:", error);
        handleError("Error verifying your account. Please try again.");
      }
    };

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user query also exposes isError for handling API failures
  const { data: currentUser, isLoading: isLoadingUser, isError: isUserError, refetch: refetchUser } =
    useGetCurrentUserQuery(undefined, {
      skip: status !== "success" && status !== "needs_job_title",
    });

  // Check if user needs to provide job title after successful auth
  useEffect(() => {
    if (status === "success" && !isLoadingUser) {
      // Handle API error or missing user data
      if (isUserError || !currentUser) {
        handleError("Failed to load your profile. Please try logging in again.");
        return;
      }

      // Check if job_title is missing (legacy user needs to provide it)
      if (!currentUser.job_title) {
        setStatus("needs_job_title");
      } else {
        // User has job title, redirect to appropriate page
        redirectAfterVerify();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, isLoadingUser, isUserError, currentUser]);

  const handleError = (message: string) => {
    if (!hasHandledError.current) {
      hasHandledError.current = true;
      setStatus("error");
      setErrorMessage(message);
      toast.showToast(message, "error");
    }
  };

  const handleOtpVerify = async () => {
    const trimmedEmail = otpEmail.trim().toLowerCase();
    const code = otpCode.trim();

    if (!trimmedEmail) {
      setOtpError("Please enter your email address");
      return;
    }

    if (code.length !== 6) {
      setOtpError("Please enter the full 6-digit code");
      return;
    }

    setIsOtpVerifying(true);
    setOtpError(null);

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: trimmedEmail,
        token: code,
        type: "email",
      });

      if (verifyError) {
        console.error("OTP verification error:", verifyError);
        setOtpError(
          verifyError.message?.includes("expired")
            ? "Code has expired. Please request a new one."
            : "Invalid code. Please check and try again."
        );
        return;
      }

      // Session established — transition to success for post-auth flow
      toast.showToast("Signed in successfully!", "success");
      setStatus("success");
    } catch (err) {
      console.error("Unexpected OTP error:", err);
      setOtpError("An unexpected error occurred. Please try again.");
    } finally {
      setIsOtpVerifying(false);
    }
  };

  const redirectAfterVerify = async () => {
    // The AI Governance Wizard was retired (G11/OPS-03). A stale link may still
    // carry ?wizard_id=; it must never block sign-in, so we log it and fall
    // through to the ordinary post-verification destination.
    if (wizardId) {
      console.info(
        "Ignoring wizard_id from the retired governance flow; continuing sign-in.",
      );
    }

    // If a return_to destination is stored, honour it even for first-login users.
    // This ensures one-click pilot flows (e.g. SPGS) land on the intended page.
    // Onboarding is informational and the user can access it later.
    const returnUrl = getReturnUrl();
    if (returnUrl !== "/courses") {
      navigate(returnUrl, { replace: true });
    } else if (currentUser && !currentUser.first_login_at) {
      window.location.assign(APP_HOME_URL);
    } else {
      navigate("/courses", { replace: true });
    }
  };

  const onJobTitleSubmitted = async () => {
    // Refetch user profile to get updated job_title
    await refetchUser();
    // Then redirect
    redirectAfterVerify();
  };

  const navigateToLogin = () => {
    window.location.href = APP_LOGIN_URL;
  };

  return {
    status,
    errorMessage,
    currentUser,
    isLoadingUser,
    onJobTitleSubmitted,
    navigateToLogin,
    // OTP entry state
    otpEmail,
    setOtpEmail,
    otpCode,
    setOtpCode,
    otpError,
    isOtpVerifying,
    handleOtpVerify,
  };
};
